import { useState, useCallback } from 'react';
import featureService from '../services/featureService';

const useFeature = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState([]);
  const [userStories, setUserStories] = useState([]);

  // Get all features for a project
  const getProjectFeatures = useCallback(async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.getProjectFeatures(projectId);
      setFeatures(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch features');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get all user stories for a project
  const getProjectUserStories = useCallback(async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.getProjectUserStories(projectId);
      setUserStories(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch user stories');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get user stories for a specific feature
  const getFeatureUserStories = useCallback(async (featureId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.getFeatureUserStories(featureId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch feature user stories');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new feature
  const createFeature = useCallback(async (projectId, featureData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.createFeature(projectId, featureData);
      setFeatures(prev => [...prev, data]);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to create feature');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a feature
  const updateFeature = useCallback(async (featureId, featureData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.updateFeature(featureId, featureData);
      setFeatures(prev => 
        prev.map(feature => 
          feature._id === featureId ? data : feature
        )
      );
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update feature');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a feature
  const deleteFeature = useCallback(async (featureId) => {
    try {
      setLoading(true);
      setError(null);
      await featureService.deleteFeature(featureId);
      setFeatures(prev => 
        prev.filter(feature => feature._id !== featureId)
      );
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete feature');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new user story
  const createUserStory = useCallback(async (featureId, storyData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.createUserStory(featureId, storyData);
      setUserStories(prev => [...prev, data]);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to create user story');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a user story
  const updateUserStory = useCallback(async (storyId, storyData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await featureService.updateUserStory(storyId, storyData);
      setUserStories(prev => 
        prev.map(story => 
          story._id === storyId ? data : story
        )
      );
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update user story');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a user story
  const deleteUserStory = useCallback(async (storyId) => {
    try {
      setLoading(true);
      setError(null);
      await featureService.deleteUserStory(storyId);
      setUserStories(prev => 
        prev.filter(story => story._id !== storyId)
      );
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete user story');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    features,
    userStories,
    getProjectFeatures,
    getProjectUserStories,
    getFeatureUserStories,
    createFeature,
    updateFeature,
    deleteFeature,
    createUserStory,
    updateUserStory,
    deleteUserStory
  };
};

export default useFeature;